import { Component, OnInit, Output, Input, EventEmitter } from '@angular/core';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import {
  BrickSharePeriod, DEFAULT_LEVEL_NAMES, DistributorSetupService, LevelNames, SetupProductOption, SetupTerritorySplit, apiError
} from '../../../top-shared/distributor-setup/distributor-setup.service';

interface ShareRow { businessUnitId: number; businessUnitName: string; percent: number; }

/**
 * One shared sales brick of a line:
 * - Default shares: how the brick's sales are split between the business units holding it, period by period (past and
 *   current). Saving a period (From - To, no To = open-ended) sets the shares for those days only; the days around it keep
 *   theirs. The holders must stay the same during the period.
 * - Product splits: a brand or item shared differently, for sales from every distributor brick.
 */
@Component({
  selector: 'crm-save-business-unit-shared-sales-bricks',
  templateUrl: './save-business-unit-shared-sales-bricks.component.html',
  styleUrls: ['./save-business-unit-shared-sales-bricks.component.css']
})
export class SaveBusinessUnitSharedSalesBricksComponent implements OnInit {
  @Output() closeEvent = new EventEmitter<boolean>();
  @Input() brickId: number;
  @Input() buLineId: number;
  @Input() brickName: string;
  @Input() buLineName: string;
  /** yyyy-MM-dd the list was shown for: its period is marked */
  @Input() asOf: string;

  names: LevelNames = DEFAULT_LEVEL_NAMES;
  periods: BrickSharePeriod[] = [];
  loadingPeriods = false;
  changed = false;

  // default shares editor
  editing = false;
  editStart: string;
  editEnd: string;
  editRows: ShareRow[] = [];
  editError = '';
  saving = false;

  constructor(private readonly alertService: AlertService,
    private readonly setupService: DistributorSetupService) { }

  ngOnInit() {
    this.setupService.levelNames().subscribe(n => this.names = n || DEFAULT_LEVEL_NAMES);
    this.loadPeriods();
    this.loadProductSplits();
    this.setupService.products().subscribe(p => this.products = p || []);
  }

  // ---- default shares, dated

  loadPeriods() {
    this.loadingPeriods = true;
    this.setupService.brickShares(this.brickId, this.buLineId).subscribe(r => {
      this.loadingPeriods = false;
      this.periods = r || [];
    }, err => { this.loadingPeriods = false; this.alertService.showMessage('', apiError(err, 'Could not load the shares.'), MessageSeverity.Error); });
  }

  /** a single holder gets all the sales whatever its stored percent (0 = "not shared") */
  shown(p: BrickSharePeriod, percent: number) { return p.onlyHolder && percent == 0 ? 100 : Math.round(percent * 100) / 100; }

  sharesText(p: BrickSharePeriod) { return p.shares.map(s => `${s.businessUnitName} ${this.shown(p, s.percent)}%`).join(' · ') + (p.onlyHolder ? ' (only holder)' : ''); }

  isAsOf(p: BrickSharePeriod) {
    return !!this.asOf && p.start.substring(0, 10) <= this.asOf && (!p.end || p.end.substring(0, 10) >= this.asOf);
  }

  /** the period holding a day (yyyy-MM-dd) */
  private periodOn(day: string) {
    return this.periods.find(p => p.start.substring(0, 10) <= day && (!p.end || p.end.substring(0, 10) >= day));
  }

  newPeriod() {
    this.editing = true;
    this.editStart = null;
    this.editEnd = null;
    this.editRows = [];
    this.editError = '';
  }

  editPeriod(p: BrickSharePeriod) {
    this.editing = true;
    this.editStart = p.start.substring(0, 10);
    this.editEnd = p.end ? p.end.substring(0, 10) : null;
    this.editRows = p.shares.map(s => ({ businessUnitId: s.businessUnitId, businessUnitName: s.businessUnitName, percent: this.shown(p, s.percent) }));
    this.editError = '';
  }

  /** the holders on the start date, with their shares of that day */
  fillRows() {
    this.editError = '';
    const p = this.editStart ? this.periodOn(this.editStart) : null;
    if (!p) {
      this.editRows = [];
      if (this.editStart) this.editError = `No ${this.names.holder.toLowerCase()} holds this brick on that day.`;
      return;
    }
    const old = this.editRows;
    this.editRows = p.shares.map(s => {
      const had = old.find(r => r.businessUnitId == s.businessUnitId);
      return { businessUnitId: s.businessUnitId, businessUnitName: s.businessUnitName, percent: had ? had.percent : this.shown(p, s.percent) };
    });
  }

  get editTotal() { return Math.round(this.editRows.reduce((s, r) => s + (Number(r.percent) || 0), 0) * 100) / 100; }

  /** equal shares of 2 decimals adding up to exactly 100: rounded down, the rounding rest to the last ones, 0.01 each (as the server) */
  equalShares() {
    const n = this.editRows.length;
    if (!n) return;
    const share = Math.floor(10000 / n);
    const rest = 10000 - share * n;
    this.editRows.forEach((r, i) => r.percent = (share + (i >= n - rest ? 1 : 0)) / 100);
  }

  saveShares() {
    if (!this.editStart) { this.editError = 'The start date is required.'; return; }
    if (this.editEnd && this.editEnd < this.editStart) { this.editError = 'The end date is before the start date.'; return; }
    if (!this.editRows.length) { this.editError = `Load the ${this.names.holders.toLowerCase()} first.`; return; }
    this.editError = '';
    this.saving = true;
    const body = {
      brickId: this.brickId, lineId: this.buLineId, start: this.editStart, end: this.editEnd || null,
      shares: this.editRows.map(r => ({ businessUnitId: r.businessUnitId, percent: Number(r.percent) || 0 }))
    };
    this.setupService.saveBrickShares(body).subscribe(() => {
      this.saving = false;
      this.editing = false;
      this.changed = true;
      this.alertService.showMessage('', 'Shares saved', MessageSeverity.Success);
      this.loadPeriods();
    }, err => { this.saving = false; this.editError = apiError(err, 'The shares could not be saved.'); });
  }

  // ---- product splits: a brand / item shared differently on this brick, whatever distributor brick it comes from
  productSplits: SetupTerritorySplit[] = [];
  products: SetupProductOption[] = [];
  productEditing = false;
  productSplit: SetupTerritorySplit = null;

  loadProductSplits() {
    this.setupService.brickProductSplits(this.brickId, this.buLineId).subscribe(r => this.productSplits = r || []);
  }

  splitText(s: SetupTerritorySplit) { return s.shares.map(x => `${x.businessUnitName} ${Math.round(x.percent * 100) / 100}%`).join(' · '); }

  editProductSplit(s: SetupTerritorySplit) { this.productSplit = s; this.productEditing = true; }

  productSplitSaved() { this.productEditing = false; this.loadProductSplits(); }

  deleteProductSplit(s: SetupTerritorySplit) {
    if (!confirm(`Delete the split of ${s.product ? s.product.name : ''} from ${s.start.substring(0, 10)}?`)) return;
    this.setupService.deleteTerritorySplit(s.ids, false).subscribe(() => this.loadProductSplits(),
      err => this.alertService.showMessage('', apiError(err, 'Could not delete.'), MessageSeverity.Error));
  }

  back() {
    this.closeEvent.emit(this.changed);
  }
}
